// =============================================================================
// QuestionButtons.tsx — Inline question buttons with answer options (web port)
// =============================================================================

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import type { QuestionObject } from '../../services/notesToHank/NotesToHankTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface QuestionButtonsProps {
  questions: QuestionObject[];
  onAnswerSelect: (
    questionIndex: number,
    answerIndex: number,
    answerId: string,
    answerText: string
  ) => void;
  selectedAnswers: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const QuestionButtons: React.FC<QuestionButtonsProps> = ({
  questions,
  onAnswerSelect,
  selectedAnswers,
}) => {
  const { theme } = useTheme();

  return (
    <Box sx={{ mt: 1 }}>
      {questions.map((q, qIdx) => (
        <Box key={q.id} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: theme.text, mb: 0.75 }}>
            {q.question}
          </Typography>

          {q.answers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {q.answers.map((answer, aIdx) => {
                const isSelected = selectedAnswers[q.id] === answer.id;
                return (
                  <Button
                    key={answer.id}
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() =>
                      onAnswerSelect(qIdx, aIdx, answer.id, answer.text)
                    }
                    aria-label={`Answer: ${answer.text}${
                      isSelected ? ', selected' : ''
                    }`}
                    sx={{
                      borderRadius: '16px',
                      textTransform: 'none',
                      fontSize: 12,
                      px: 1.5,
                      py: 0.5,
                      minWidth: 'auto',
                      backgroundColor: isSelected
                        ? theme.primary
                        : theme.background3,
                      color: isSelected ? theme.textCTA : theme.text,
                      borderColor: 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? theme.primaryDark
                          : theme.background2,
                      },
                    }}
                  >
                    {answer.text}
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default QuestionButtons;
